// `RowMenu` (design-system spec §3.17, D-448 ruling 2): a row's secondary
// actions behind one "More" button.
//
// Waste settings drew Customize (or Edit) and Turn off stacked on every row:
// 2,167 buttons over 1,081 rows on staging. A row now carries its one primary
// action inline and everything else here. It is a disclosure, not an ARIA
// menu: the panel holds ordinary buttons (or links) in the reading order after
// the trigger, so a keyboard user tabs into them and a walkthrough finds them
// by their role and name once the panel is open. Escape, a press outside and
// choosing an action close it, and Escape gives focus back to the trigger.
//
// Written in the waste area (UA-waste phase 2) and moved here unchanged apart
// from its class names, so every area shares one implementation. A row whose
// reader may do nothing secondary passes no children, and then no trigger is
// drawn: a read-only role sees no "More" button at all.
//
// The panel is placed with fixed coordinates taken from the trigger, not
// absolutely inside the row. The table wrapper scrolls sideways, which makes it
// a scroll box both ways, so an absolute panel on the last rows (the system
// rows always sort last) opened inside that box and was cut off. Near the
// bottom of the window the panel opens upward, and it follows the trigger when
// the page or the table scrolls.

import { Children, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

/** Room the panel needs below the trigger before it opens upward instead: two
 * buttons and the panel's padding, used until the panel has been measured. */
const MIN_PANEL_PX = 96;
const GAP_PX = 4;

/** Where the panel goes, from the trigger's box and the window's size. */
export function rowMenuPlacement(
  trigger: { top: number; bottom: number; right: number },
  panelHeight: number,
  viewport: { width: number; height: number },
): CSSProperties {
  const needed = Math.max(panelHeight, MIN_PANEL_PX) + GAP_PX;
  const below = viewport.height - trigger.bottom;
  const right = Math.max(viewport.width - trigger.right, 0);
  if (needed > below && trigger.top > below) {
    return {
      position: "fixed",
      top: "auto",
      right,
      bottom: viewport.height - trigger.top + GAP_PX,
      marginTop: 0,
    };
  }
  return { position: "fixed", top: trigger.bottom + GAP_PX, right, marginTop: 0 };
}

export interface RowMenuProps {
  /** The trigger's accessible name. Screens use the core key
   * `nav.moreActionsFor` with the row's code or name, so two rows never share
   * a name ("More actions for COA-2026-00007"). */
  label: string;
  /** The panel's buttons or links. A press on any of them closes the panel.
   * With none (every child null or false) the menu renders nothing. */
  children: ReactNode;
  /** The trigger's test id (default `row-menu`); the panel is that id plus
   * `-panel`. */
  "data-testid"?: string;
}

export function RowMenu({ label, children, ...rest }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined);
  const testId = rest["data-testid"] ?? "row-menu";
  const empty = Children.toArray(children).length === 0;

  useLayoutEffect(() => {
    if (!open) {
      setPlace(undefined);
      return undefined;
    }
    const measure = (): void => {
      const box = trigger.current?.getBoundingClientRect();
      if (box === undefined) return;
      setPlace(
        rowMenuPlacement(box, panel.current?.offsetHeight ?? 0, {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    };
    measure();
    // Capture, so a scroll inside the table's own box moves the panel too.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        // One press closes one layer: a surrounding panel (FilterBar's, a
        // SidePeek) sees the key as handled and stays open. The listener is
        // in the capture phase because theirs are on the document's bubble
        // phase and were added first, so a bubble listener here would run
        // after them.
        e.preventDefault();
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (empty) return null;

  return (
    <div className="ds-row-menu" ref={root}>
      <button
        type="button"
        ref={trigger}
        className="ds-btn ds-btn-sm ds-row-menu-trigger"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="3.5" cy="8" r="1.25" />
          <circle cx="8" cy="8" r="1.25" />
          <circle cx="12.5" cy="8" r="1.25" />
        </svg>
      </button>
      {open ? (
        <div
          id={panelId}
          ref={panel}
          className="ds-row-menu-panel"
          style={place}
          data-testid={`${testId}-panel`}
          onClick={(e) => {
            if ((e.target as Element).closest("button, a[href]") === null) return;
            // Focus goes to the trigger before the panel closes. The action's
            // own handler has already run, and a dialog it opens records the
            // focused element when it mounts, to return focus there on close.
            // Left on the pressed button, focus would fall to the page when the
            // panel unmounts it.
            trigger.current?.focus();
            setOpen(false);
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
